import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type {
  HardwarePreset,
  HardwarePresetInsert,
  HardwarePresetUpdate,
} from '../lib/database.types'

export const HARDWARE_PRESETS_KEY = ['hardware_presets']

export function useHardwarePresets() {
  return useQuery({
    queryKey: HARDWARE_PRESETS_KEY,
    queryFn: async (): Promise<HardwarePreset[]> => {
      const { data, error } = await supabase
        .from('hardware_presets')
        .select('*')
        .order('name', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreateHardwarePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<HardwarePresetInsert, 'user_id'>): Promise<HardwarePreset> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { data, error } = await supabase
        .from('hardware_presets')
        .insert({ ...input, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HARDWARE_PRESETS_KEY }),
  })
}

export function useUpdateHardwarePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & HardwarePresetUpdate): Promise<HardwarePreset> => {
      const { data, error } = await supabase
        .from('hardware_presets')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HARDWARE_PRESETS_KEY }),
  })
}

export function useDeleteHardwarePreset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('hardware_presets').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HARDWARE_PRESETS_KEY }),
  })
}
